import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

export const ParticipationCharts = ({ onDrilldown }) => {
  const { branchLock } = useAuth();
  const { currentBranchFilter, setCurrentBranchFilter, getScopedStudents, participationCounts } = useData();

  const scoped = getScopedStudents();
  const getBarColor = (pct) => {
    if (pct >= 90) return '#6366f1';
    if (pct >= 60) return '#f97316';
    return '#ef4444';
  };

  // Grade-wise Bar Chart
  const gradeGroups = {};
  scoped.forEach(student => {
    const grade = student.registeredGrade;
    if (!gradeGroups[grade]) {
      gradeGroups[grade] = { participated: 0, total: 0 };
    }
    gradeGroups[grade].total++;
    if (student.participated) gradeGroups[grade].participated++;
  });
  const chartGrades = Object.keys(gradeGroups).sort((a, b) => parseInt(a) - parseInt(b));
  // const gradeRates = chartGrades.map(g =>
  //   gradeGroups[g].total > 0 ? parseFloat(((gradeGroups[g].participated / gradeGroups[g].total) * 100).toFixed(1)) : 0
  // );

  const gradeData = Object.values( participationCounts.filter((item) => item.Grade !== "Total")
    .reduce((acc, item) => {
      const grade = item.Grade;

      if (!acc[grade]) {
        acc[grade] = {
          Grade: grade,
          Registered: 0,
          Participated: 0,
          Absent: 0,
        };
      }
      acc[grade].Registered += Number(item.Registered || 0);
      acc[grade].Participated += Number(item.Participated || 0);
      acc[grade].Absent += Number(item.Absent || 0);
      return acc;
    }, {})
  );

  const gradeRates = gradeData.map((item) =>
    item.Registered > 0 ? parseFloat(((item.Participated / item.Registered) * 100).toFixed(1)) : 0
  );
  const CGrades = [...new Set(
    participationCounts
      .filter((G) => G.Grade !== "Total")
      .map((k) => k.Grade)
  )]
  localStorage.setItem("NONP", CGrades.filter((value, index) => gradeRates[index] === 0))
  // localStorage.setItem("NONP", CGrades.filter((value, index) => gradeRates[index] === 0)?.map((item) => item.Grade))
  // localStorage.setItem("NONP",chartGrades.filter((value, index) => gradeRates[index] === 0))
  const gradeBarData = {
    // labels:CGrades,
    labels: chartGrades.map(g => `Grade ${g}`),
    datasets: [
      {
        type: 'bar',
        label: 'Participation %',
        data: gradeRates,
        backgroundColor: gradeRates.map(v => getBarColor(v)),
        borderRadius: 6
      }
    ]
  };

  const barOptions = (chartType, onClickHandler) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: '#f3f4f6' },
        ticks: { color: '#6b7280', font: { size: 11 } }
      },
      x: {
        grid: { display: false },
        ticks: { color: '#374151', font: { size: 11, weight: 'bold' } }
      }
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end',
        align: 'top',
        color: '#4b5563',
        formatter: (val) => `${Math.round(val)}%`,
        font: { weight: 'bold', size: 11 }
      },
      tooltip: {
        callbacks: {
          label: (c) => {
            const index = c.dataIndex;
            if (chartType === 'grade') {
              const key = chartGrades[index];
              const gInfo = gradeGroups[key];
              return [
                `Percentage: ${c.parsed.y}%`,
                `Participated: ${gInfo ? gInfo.participated : 0}`,
                `Total Registered: ${gInfo ? gInfo.total : 0}`
              ];
            }
            if (chartType === 'branch') {
              const bKey = chartBranches[index];
              const bInfo = branchGroups[bKey];
              return [
                `Percentage: ${c.parsed.y}%`,
                `Participated: ${bInfo ? bInfo.participated : 0}`,
                `Total Registered: ${bInfo ? bInfo.total : 0}`
              ];
            }
            if (chartType === 'section') {
              const sKey = sectionKeys[index];
              const sInfo = sectionGroups[sKey];
              return [
                `Percentage: ${c.parsed.y}%`,
                `Participated: ${sInfo ? sInfo.participated : 0}`,
                `Total Registered: ${sInfo ? sInfo.total : 0}`
              ];
            }
            return `Percentage: ${c.parsed.y}%`;
          }
        }
      }
    },
    onClick: (event, activeElements) => {
      if (activeElements.length > 0) {
        const index = activeElements[0].index;
        onClickHandler(index);
      }
    }
  });

  const handleGradeClick = (index) => {
    const gradeNum = parseInt(chartGrades[index]);
    onDrilldown({ type: 'grade', value: gradeNum });
  };

  // Branch-wise Bar Chart (Admin only when "All Branches" is selected)
  const showBranchChart = !branchLock && currentBranchFilter === 'all';
  const branchGroups = {};
  if (showBranchChart) {
    scoped.forEach(student => {
      const branch = student.school || 'Unknown';
      if (!branchGroups[branch]) {
        branchGroups[branch] = { participated: 0, total: 0 };
      }
      branchGroups[branch].total++;
      if (student.participated) branchGroups[branch].participated++;
    });
  }

  const chartBranches = Object.keys(branchGroups).sort();
  const branchRates = chartBranches.map(b =>
    branchGroups[b].total > 0 ? parseFloat(((branchGroups[b].participated / branchGroups[b].total) * 100).toFixed(1)) : 0
  );

  const branchBarData = {
    labels: chartBranches,
    datasets: [
      {
        type: 'bar',
        data: branchRates,
        backgroundColor: branchRates.map(v => getBarColor(v)),
        borderRadius: 6
      }
    ]
  };

  const handleBranchClick = (index) => {
    const branchName = chartBranches[index];
    setCurrentBranchFilter(branchName);
  };

  // Section-wise Bar Chart (Scoped school mode only)
  const showSectionChart = branchLock || currentBranchFilter !== 'all';
  const sectionGroups = {};
  if (showSectionChart) {
    scoped.forEach(student => {
      const grade = student.registeredGrade;
      const section = student.section || 'Unassigned';
      const key = `${grade}-${section}`;
      if (!sectionGroups[key]) {
        sectionGroups[key] = { grade, section, participated: 0, total: 0 };
      }
      sectionGroups[key].total++;
      if (student.participated) sectionGroups[key].participated++;
    });
  }

  const sectionKeys = Object.keys(sectionGroups).sort((a, b) => {
    const ga = sectionGroups[a];
    const gb = sectionGroups[b];
    return ga.grade - gb.grade || String(ga.section).localeCompare(String(gb.section));
  });

  // const sectionRates = sectionKeys.map(k =>
  //   sectionGroups[k].total > 0 ? parseFloat(((sectionGroups[k].participated / sectionGroups[k].total) * 100).toFixed(1)) : 0
  // );
  const sectionRates = participationCounts.filter((G) => G.Grade !== "Total").map((K) =>
    K.Registered > 0 ? parseFloat(((K.Participated / K.Registered) * 100).toFixed(1)) : 0
  );

  const sectionBarData = {
    labels: participationCounts.filter((G) => G.Grade !== "Total").map(k => `Grade ${k.GradeSection}`),
    // labels: sectionKeys.map(k => `Grade ${sectionGroups[k].grade} - ${sectionGroups[k].section}`),
    datasets: [
      {
        type: 'bar',
        data: sectionRates,
        backgroundColor: sectionRates.map(v => getBarColor(v)),
        borderRadius: 6
      }
    ]
  };

  const handleSectionClick = (index) => {
    const groupKey = sectionKeys[index];
    const group = sectionGroups[groupKey];
    onDrilldown({
      type: 'gradeSection',
      value: { grade: group.grade, section: group.section }
    });
  };

  return (
    <div className="space-y-6 select-none">
      {/* Branch-wise Participation Card (Admin mode) */}
      {showBranchChart && (
        <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-6">
          <div className="flex justify-between items-start mb-4 flex-wrap gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Branch-wise Participation</h3>
              <p className="text-sm text-gray-600">
                Percentage of student participation per branch (Click a bar to scope the whole dashboard to that branch)
              </p>
            </div>
            {/* Legend */}
            <div className="flex flex-col gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#6366f1]"></span>
                <span>&ge; 90% (Excellent)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#f97316]"></span>
                <span>60% - 89% (Average)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#ef4444]"></span>
                <span>&lt; 60% (Low)</span>
              </div>
            </div>
          </div>
          <div className="chart-wrapper" style={{ position: 'relative', height: '320px' }}>
            <Bar data={branchBarData} options={barOptions('branch', handleBranchClick)} />
          </div>
        </div>
      )}

      {/* Grade-wise Participation Card */}
      <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-6">
        <div className="flex justify-between items-start mb-4 flex-wrap gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Grade-wise Participation  {localStorage.getItem("NONP") && `- ${localStorage.getItem("NONP")} are not Participated`} </h3>
            <p className="text-sm text-gray-600">
              Percentage of student participation per grade (Click on a bar to view students)
            </p>
          </div>
          {/* Legend */}
          <div className="flex flex-col gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#6366f1]"></span>
              <span>&ge; 90% (Excellent)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#f97316]"></span>
              <span>60% - 89% (Average)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#ef4444]"></span>
              <span>&lt; 60% (Low)</span>
            </div>
          </div>
        </div>
        {/* <div className="chart-wrapper" style={{ position: 'relative', height: '320px' }}>
          <Bar data={gradeBarData} options={barOptions('grade', handleGradeClick)} />
        </div> */}
        <div className="chart-wrapper w-full max-w-full overflow-hidden" style={{ position: 'relative', height: '320px', minWidth: 0 }}>
          <div style={{ position: 'absolute', inset: 0, minWidth: 0 }}>
            <Bar data={gradeBarData} options={barOptions('grade', handleGradeClick)} />
          </div>
        </div>
      </div>

      {/* Section-wise Participation Card (Scoped mode) */}
      {showSectionChart && (
        <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-6">
          <div className="flex justify-between items-start mb-4 flex-wrap gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Section-wise Participation</h3>
              <p className="text-sm text-gray-600">
                Percentage of student participation per grade &amp; section (Click on a bar to view students)
              </p>
            </div>
            {/* Legend */}
            <div className="flex flex-col gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#6366f1]"></span>
                <span>&ge; 90% (Excellent)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#f97316]"></span>
                <span>60% - 89% (Average)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#ef4444]"></span>
                <span>&lt; 60% (Low)</span>
              </div>
            </div>
          </div>
          <div className="chart-wrapper" style={{ position: 'relative', height: '320px' }}>
            <Bar data={sectionBarData} options={barOptions('section', handleSectionClick)} />
          </div>
        </div>
      )}
    </div>
  );
};
export default ParticipationCharts;
